import { DragEvent, TouchEvent, useEffect, useMemo, useRef, useState } from "react";
import Controls from "../components/Controls";
import Progress from "../components/Progress";
import RoomHeader from "../components/RoomHeader";
import { useTranslation } from "../components/Localization";
import { usePageGame } from "../hooks/usePageGame";
import { saveGameScore } from "../services/gameScores";

type Placements = Record<string, string>;
type TouchDrag = { value: string; x: number; y: number; startX: number; startY: number; moved: boolean };

export function matchingScoreFromErrors(errorCount: number) {
  if (errorCount <= 1) return 15;
  if (errorCount === 2) return 12;
  if (errorCount === 3) return 9;
  return 6;
}

export default function MatchingGamePage() {
  const { room, t } = useTranslation();
  const { game } = usePageGame("talalo", "match_image_text");
  const events = useMemo(() => {
    const pairIndexes = Array.from(new Set((game?.items || []).map((item) => item.pairIndex).filter((value): value is number => value !== null))).sort((a, b) => a - b);
    return pairIndexes.flatMap((pairIndex) => {
      const items = game?.items.filter((item) => item.pairIndex === pairIndex) || [];
      const image = items.find((item) => item.kind === "image" && item.url);
      const text = items.find((item) => item.kind === "text" && item.textContent);
      return image?.url && text?.textContent ? [{ id: String(pairIndex), value: text.textContent, image: image.url }] : [];
    });
  }, [game]);
  const [placements, setPlacements] = useState<Placements>({});
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [draggingValue, setDraggingValue] = useState<string | null>(null);
  const [touchDrag, setTouchDrag] = useState<TouchDrag | null>(null);
  const [errorCount, setErrorCount] = useState(0);
  const [score, setScore] = useState<number | null>(null);
  const suppressClick = useRef(false);
  const errorCountRef = useRef(0);
  const completedRef = useRef(false);

  useEffect(() => {
    const values = events.map((event) => event.value);
    setPlacements(Object.fromEntries(events.map((event, index) => [event.id, values[(index + 1) % values.length]])));
    setSelectedValue(null);
    setErrorCount(0);
    setScore(null);
    errorCountRef.current = 0;
    completedRef.current = false;
  }, [events]);

  const complete = events.length > 0 && events.every((event) => placements[event.id] === event.value);

  const placeValue = (eventId: string, value: string) => {
    const sourceId = Object.keys(placements).find((key) => placements[key] === value);
    if (!sourceId || sourceId === eventId) return;
    const next = { ...placements, [sourceId]: placements[eventId], [eventId]: value };
    if (value !== events.find((item) => item.id === eventId)?.value) {
      errorCountRef.current = Math.min(4, errorCountRef.current + 1);
      setErrorCount(errorCountRef.current);
    }
    if (events.every((item) => next[item.id] === item.value) && !completedRef.current) {
      completedRef.current = true;
      const finalScore = matchingScoreFromErrors(errorCountRef.current);
      saveGameScore("matching", finalScore);
      setScore(finalScore);
    }
    setPlacements(next);
    setSelectedValue(null);
  };

  const dropValue = (event: DragEvent<HTMLElement>, eventId: string) => {
    event.preventDefault();
    const value = event.dataTransfer.getData("text/plain");
    if (events.some((item) => item.value === value)) placeValue(eventId, value);
  };

  const startTouchDrag = (event: TouchEvent<HTMLButtonElement>, value: string) => {
    const touch = event.touches[0];
    setTouchDrag({ value, x: touch.clientX, y: touch.clientY, startX: touch.clientX, startY: touch.clientY, moved: false });
  };
  const moveTouchDrag = (event: TouchEvent<HTMLButtonElement>) => {
    if (!touchDrag) return;
    event.preventDefault();
    const touch = event.touches[0];
    setTouchDrag({ ...touchDrag, x: touch.clientX, y: touch.clientY, moved: touchDrag.moved || Math.hypot(touch.clientX - touchDrag.startX, touch.clientY - touchDrag.startY) > 6 });
  };
  const finishTouchDrag = (event: TouchEvent<HTMLButtonElement>) => {
    if (!touchDrag) return;
    const touch = event.changedTouches[0];
    if (touchDrag.moved) {
      const target = document.elementFromPoint(touch.clientX, touch.clientY)?.closest<HTMLElement>(".matching-card");
      if (target?.dataset.eventId) placeValue(target.dataset.eventId, touchDrag.value);
      suppressClick.current = true;
    }
    setTouchDrag(null);
  };

  return <main className="screen activity matching-page">
    <RoomHeader roomId="dining" />
    <p className="instruction">{room("dining")?.tasks.matching}</p>
    <div className="matching-game"><div className="matching-cards">
      {events.map((item, index) => {
        const placedValue = placements[item.id] || "";
        const correct = placedValue === item.value;
        const label = t("accessibility.matchingEvent", { year: item.value });
        return <article className="matching-card" key={item.id} data-event-id={item.id} onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropValue(event, item.id)}>
          <div className="matching-image-frame"><img className={`matching-image matching-image-${index + 1}`} src={item.image} alt={label} /></div>
          <button type="button" draggable className={`matching-dropzone ${correct ? "is-correct" : "is-wrong"} ${selectedValue === placedValue ? "is-selected" : ""} ${draggingValue === placedValue ? "is-drag-source" : ""}`} onDragStart={(event) => { event.dataTransfer.setData("text/plain", placedValue); setDraggingValue(placedValue); }} onDragEnd={() => setDraggingValue(null)} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); event.stopPropagation(); dropValue(event, item.id); setDraggingValue(null); }} onTouchStart={(event) => startTouchDrag(event, placedValue)} onTouchMove={moveTouchDrag} onTouchEnd={finishTouchDrag} onClick={() => { if (suppressClick.current) suppressClick.current = false; else selectedValue ? placeValue(item.id, selectedValue) : setSelectedValue(placedValue); }} aria-label={t("accessibility.matchingMove", { year: placedValue, label })}>{placedValue}</button>
        </article>;
      })}
    </div></div>
    <div className="matching-score" aria-live="polite">{score !== null ? t("score.gameResult", { score, max: 15 }) : t("score.mistakes", { count: errorCount, max: 4 })}</div>
    {draggingValue && <style>{`.matching-dropzone:not(.is-drag-source) { pointer-events: none; }`}</style>}
    {touchDrag?.moved && <div className="matching-touch-ghost" style={{ left: touchDrag.x, top: touchDrag.y }} aria-hidden="true">{touchDrag.value}</div>}
    <Progress step={1} total={4} icon="center-up" />
    <Controls backTo="/talalo/feladat" nextTo="/talalo/restauralas" check={!complete} nextDisabled={!complete} />
  </main>;
}
